
import { useState } from "react";
import { DashboardTrip, TRIPS_PER_PAGE } from "@/types/dashboard";
import TripCard from "@/components/TripCard";
import { 
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious
} from "@/components/ui/pagination";

interface TripListingProps {
  trips: DashboardTrip[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onClaimTrip?: (tripId: string) => void;
}

const TripListing = ({ 
  trips, 
  isLoading, 
  isError, 
  error, 
  onClaimTrip 
}: TripListingProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  
  const totalPages = Math.ceil(trips.length / TRIPS_PER_PAGE);
  const startIndex = (currentPage - 1) * TRIPS_PER_PAGE;
  const endIndex = startIndex + TRIPS_PER_PAGE;
  const currentTrips = trips.slice(startIndex, endIndex);
  
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };
  
  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <p className="text-gray-500">Loading trips...</p>
      </div>
    );
  }
  
  if (isError) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <p className="text-red-500">Error loading trips: {error instanceof Error ? error.message : "Unknown error"}</p>
      </div>
    );
  }
  
  if (currentTrips.length === 0) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <p className="text-gray-500">No trips available</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
        {currentTrips.map(trip => (
          <TripCard 
            key={trip.id} 
            {...trip} 
            onClaimTrip={onClaimTrip ? () => onClaimTrip(trip.id) : undefined}
            status={trip.status}
          />
        ))}
      </div>
      
      {totalPages > 1 && (
        <Pagination className="mt-8">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={goToPrevPage}
                className={`${currentPage <= 1 ? 'opacity-50 pointer-events-none' : ''} text-black`}
                aria-disabled={currentPage <= 1}
              />
            </PaginationItem>
            <PaginationItem>
              <span className="flex items-center mx-2 text-sm font-medium text-black">
                Page {currentPage} of {totalPages}
              </span>
            </PaginationItem>
            <PaginationItem>
              <PaginationNext 
                onClick={goToNextPage}
                className={`${currentPage >= totalPages ? 'opacity-50 pointer-events-none' : ''} text-black`}
                aria-disabled={currentPage >= totalPages}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </>
  );
};

export default TripListing;
